import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CONVERSATIONS } from "@/constants/testIds";

// Fix AG (2026-10-21): la misma ventana edita las etapas y los servicios de interes.
const KINDS = {
  tags: {
    title: "Etiquetas", endpoint: "/conversations/tags", field: "tags", max: 40, placeholder: "Etiqueta nueva", locked: [],
    description: "Las ocultas no aparecen para agregar a un chat, pero los chats que ya la tienen la conservan y se puede filtrar por ellas. Solo se borra una etiqueta que no está en ningún chat.",
  },
  stages: {
    title: "Etapas", endpoint: "/conversations/stages", field: "stages", max: 60, placeholder: "Etapa nueva", locked: ["nuevo"],
    description: "\"Nuevo\" es la etapa de los chats sin clasificar: se puede renombrar, pero no ocultar ni borrar. Las ocultas no se ofrecen para otros chats y solo se borra una etapa que no está en ningún chat.",
  },
  interests: {
    title: "Servicios de interés", endpoint: "/conversations/interests", field: "interests", max: 60, placeholder: "Servicio nuevo", locked: [],
    description: "Es solo para clasificar los chats: no cambia los servicios de la agenda ni de la reserva online. Los ocultos no se ofrecen para otros chats y solo se borra uno que no está en ningún chat.",
  },
};

/** Fix AE (2026-10-19): el equipo edita la lista de etiquetas de los chats:
 * nombre, orden, ocultar (no se ofrece para chats nuevos; los que ya la tienen
 * la conservan) y borrar (solo si ningun chat la tiene). */
export default function TagManagerDialog({ open, onOpenChange, tags, onSaved, kind = "tags" }) {
  const cfg = KINDS[kind] || KINDS.tags;
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRows(tags.map((t) => ({ ...t, uid: t.key })));
    setNewLabel("");
    setError("");
  }, [open, tags]);

  const update = (i, changes) => setRows((r) => r.map((x, j) => (j === i ? { ...x, ...changes } : x)));
  const move = (i, d) => setRows((r) => {
    const j = i + d;
    if (j < 0 || j >= r.length) return r;
    const n = [...r];
    [n[i], n[j]] = [n[j], n[i]];
    return n;
  });
  const add = () => {
    const label = newLabel.trim();
    if (!label) return;
    setRows((r) => [...r, { key: null, label, hidden: false, uid: `nueva-${Date.now()}` }]);
    setNewLabel("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(cfg.endpoint, { [cfg.field]: rows.map((r) => ({ key: r.key || null, label: r.label, hidden: r.hidden })) });
      onSaved(data[cfg.field]);
      onOpenChange(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid={kind === "tags" ? CONVERSATIONS.tagsDialog : `${CONVERSATIONS.tagsDialog}-${kind}`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{cfg.title}</DialogTitle>
          <DialogDescription>{cfg.description}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-1">
          {rows.map((r, i) => (
            <div key={r.uid} className={`flex items-center gap-1 ${r.hidden ? "opacity-60" : ""}`} data-testid={`${CONVERSATIONS.tagRowPrefix}${i}`}>
              <Input value={r.label} maxLength={cfg.max} onChange={(e) => update(i, { label: e.target.value })} className="h-8 text-sm flex-1" data-testid={`${CONVERSATIONS.tagLabelInputPrefix}${i}`} />
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} title="Subir" data-testid={`${CONVERSATIONS.tagUpPrefix}${i}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={i === rows.length - 1} onClick={() => move(i, 1)} title="Bajar" data-testid={`${CONVERSATIONS.tagDownPrefix}${i}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={cfg.locked.includes(r.key)} onClick={() => update(i, { hidden: !r.hidden })} title={r.hidden ? "Mostrar" : "Ocultar"} data-testid={`${CONVERSATIONS.tagHidePrefix}${i}`}>
                {r.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-8 w-8" disabled={cfg.locked.includes(r.key)} onClick={() => setRows((x) => x.filter((_, j) => j !== i))} title="Borrar" data-testid={`${CONVERSATIONS.tagDeletePrefix}${i}`}><Trash2 className="w-3.5 h-3.5 text-destructive" /></Button>
            </div>
          ))}
          <div className="flex items-center gap-1 pt-2">
            <Input value={newLabel} maxLength={cfg.max} placeholder={cfg.placeholder} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} className="h-8 text-sm flex-1" data-testid={CONVERSATIONS.tagNewInput} />
            <Button type="button" variant="outline" size="sm" onClick={add} disabled={!newLabel.trim()} data-testid={CONVERSATIONS.tagAddButton}><Plus className="w-3.5 h-3.5" /> Agregar</Button>
          </div>
          {error && <p className="text-sm text-destructive pt-1" data-testid={CONVERSATIONS.tagsError}>{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-hover text-white" data-testid={CONVERSATIONS.tagsSaveButton}>{saving ? "Guardando..." : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
